/**
 * The boot-time reconciliation between localStorage and `/api/settings`. Renders nothing.
 *
 * ## Theme and density: localStorage is the fast path, the server is the durable one
 *
 * `110-design-system.md` §2.1 settles this and §4 repeats it: theme and density must be readable
 * **synchronously, before first paint** (the blocking script in `index.html` stamps `data-theme`
 * / `data-density` on `<html>`), which rules out TanStack Query and IndexedDB alike. So
 * localStorage owns the read path. They are *also* persisted to `/api/settings` "so a new browser
 * picks it up".
 *
 * Two directions, and keeping them from fighting each other is the whole content of this file:
 *
 *  - **Local → server.** `useThemePreference()` / `useDensityPreference()` (`preferences.ts`)
 *    write localStorage *and* enqueue a `settings.patch`. Every control that changes a theme or a
 *    density goes through them — the top bar's cycle button, the palette's two commands,
 *    `Mod+Shift+D`, and the settings screen.
 *  - **Server → local, once.** This component adopts the server's values on the first successful
 *    settings load *of a session*, which is what makes a new browser inherit them. Guarded by a
 *    ref: adopting on every render would mean a local change is immediately overwritten by the
 *    cache it just wrote, and the control would spring back under the cursor.
 *
 * ## The timezone seed
 *
 * `100-settings.md` §1: `calendar.timezone` has no static default. Fixtures seed it to `UTC` so
 * the row is never missing; on first boot the client detects the browser's zone and PATCHes it
 * **only if the stored value is still the untouched seed**. That guard is not fussiness — it is
 * what stops a laptop taken abroad from silently re-homing the user's whole calendar.
 */
import { useEffect, useRef } from 'react';
import { TIMEZONE_SEED, detectTimezone, resolveSettings } from '@/api/endpoints/settings';
import { setLang as setLocalLanguage } from '@/lib/i18n';
import { setDensity as setLocalDensity } from '@/stores/density';
import { useTheme } from '@/stores/theme';
import { usePatchSettings, useSettingsQuery } from '@/features/settings/useSettings';

export function SettingsBridge(): null {
  const query = useSettingsQuery();
  const patch = usePatchSettings();
  const { setTheme: setLocalTheme } = useTheme();

  const adopted = useRef(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (query.data === undefined) return;
    const settings = resolveSettings(query.data);

    /* ── Server → local, exactly once per session ─────────────────────────── */
    if (!adopted.current) {
      adopted.current = true;

      // Only adopt what the server actually SENT. A 200 that omits `ui` (a partially seeded
      // database — `100` §2 says that must not break anything) would otherwise stamp the
      // registry default over a choice this browser has already made locally.
      if (query.data.ui?.theme !== undefined) setLocalTheme(settings.ui.theme);
      if (query.data.ui?.density !== undefined) setLocalDensity(settings.ui.density);
      if (query.data.ui?.language !== undefined) setLocalLanguage(settings.ui.language);
    }

    /* ── The timezone seed (`100` §1) ─────────────────────────────────────── */
    if (!seeded.current && settings.calendar.timezone === TIMEZONE_SEED) {
      seeded.current = true;
      const detected = detectTimezone();
      // If the browser genuinely IS on UTC there is nothing to write, and writing it anyway
      // would turn the seed into a user choice — permanently disabling this detection.
      if (detected !== TIMEZONE_SEED) patch({ calendar: { timezone: detected } });
    }
  }, [patch, query.data, setLocalTheme]);

  return null;
}
